import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Alert, Button, Form, Modal, Spinner } from "react-bootstrap";
import { AgGridReact } from "ag-grid-react";
import "ag-grid-community/styles/ag-grid.css";
import "ag-grid-community/styles/ag-theme-quartz.css";
import { Context } from "../App";
import AdminLayout from "../components/AdminLayout";
import AdminDateInput from "../components/AdminDateInput";
import * as Constants from "../api/constants";
import "../adminConsoleTheme.css";

const ROLE_HEADER = "X-Admin-Role";
const ADMIN = "ADMIN";
const SUPER_ADMIN = "SUPER_ADMIN";
const ADMIN_ROLES = new Set([ADMIN, SUPER_ADMIN]);

const FUNCTION_HALL_OPTIONS = ["Platinum", "Gold", "Diamond"];
const BANQUET_SLOT_OPTIONS = ["Lunch", "Dinner"];
const BANQUET_ADDON_OPTIONS = ["Cold Fire", "Fog Effect", "Welcome Board", "Car Entry", "Photography", "Other"];

function hallSelectOptions(currentValue) {
  const cur = String(currentValue || "").trim();
  const extras =
    cur && !FUNCTION_HALL_OPTIONS.includes(cur) ? [{ value: cur, label: `${cur} (imported)` }] : [];
  return [...FUNCTION_HALL_OPTIONS.map((h) => ({ value: h, label: h })), ...extras];
}

function slotSelectOptions(currentValue) {
  const cur = String(currentValue || "").trim();
  const extras =
    cur && !BANQUET_SLOT_OPTIONS.includes(cur) ? [{ value: cur, label: `${cur} (imported)` }] : [];
  return [...BANQUET_SLOT_OPTIONS.map((s) => ({ value: s, label: s })), ...extras];
}

function formatInr(n) {
  const v = Number(n) || 0;
  return `₹${v.toLocaleString("en-IN")}`;
}

function formatRupeeMaybe(n) {
  if (n === null || n === undefined) return "—";
  return formatInr(n);
}

function buildTelLink(phone) {
  const digits = String(phone || "").replace(/\D/g, "");
  if (!digits) return null;
  const prefix = digits.length === 10 ? "+91" : "+";
  return `tel:${prefix}${digits}`;
}

function BanquetCustomerCell({ data }) {
  if (!data) return null;
  const ref = String(data.bookingReferenceId || "").trim();
  return (
    <div className="admin-console-grid-cell-inner">
      <div className="admin-console-grid-cell-name">{data.customerName || "—"}</div>
      {ref ? (
        <div className="admin-console-grid-cell-subline">Booking ref: {ref}</div>
      ) : null}
    </div>
  );
}

function BanquetContactCell({ data }) {
  if (!data) return null;
  const tel = buildTelLink(data.customerPhone);
  const email = String(data.customerEmail || "").trim();
  return (
    <div className="admin-console-grid-cell-inner small">
      <div>
        {tel ? (
          <a href={tel} className="admin-console-grid-cell-phone" onClick={(e) => e.stopPropagation()}>
            {data.customerPhone}
          </a>
        ) : (
          <span className="admin-console-grid-cell-phone">{data.customerPhone || "—"}</span>
        )}
      </div>
      {email ? (
        <div>
          <a
            href={`mailto:${email}`}
            className="admin-console-grid-cell-contact-sub"
            onClick={(e) => e.stopPropagation()}
          >
            {email}
          </a>
        </div>
      ) : null}
    </div>
  );
}

function BanquetHallCell({ data }) {
  if (!data) return null;
  const hall = String(data.packageName || "").trim();
  const slot = String(data.slotDesc || "").trim();
  return (
    <div className="admin-console-grid-cell-inner small">
      <div className="admin-console-grid-cell-phone">{hall || "—"}</div>
      {slot ? <div className="admin-console-grid-cell-contact-sub">{slot}</div> : null}
    </div>
  );
}

function BanquetActionsCell({ data, context }) {
  if (!data?.id) return null;
  const { onEditRow, onSendConfirmation, onCancelRow, onPostponeRow, sendingId, linesBusy, isSuperAdmin } =
    context || {};
  const hasEmail = Boolean(String(data.customerEmail || "").trim());
  const sending = sendingId === data.id;
  const cancelled = Boolean(data.cancelled);

  const stopClick = (e) => {
    e.stopPropagation();
    e.preventDefault();
  };

  return (
    <div className="admin-console-grid-actions flex-column align-items-stretch gap-1">
      {isSuperAdmin ? (
        <button
          type="button"
          className="btn btn-sm admin-console-grid-btn-gold"
          disabled={linesBusy}
          onClick={(e) => {
            stopClick(e);
            onEditRow?.(data);
          }}
        >
          Edit
        </button>
      ) : null}
      <button
        type="button"
        className="btn btn-sm admin-console-grid-btn-gold"
        disabled={!hasEmail || sending || linesBusy}
        title={hasEmail ? "Send confirmation email" : "Add a customer email to enable"}
        onClick={(e) => {
          stopClick(e);
          onSendConfirmation?.(data.id);
        }}
      >
        {sending ? "Sending…" : "Email"}
      </button>
      {isSuperAdmin && !cancelled ? (
        <>
          <button
            type="button"
            className="btn btn-sm btn-outline-warning"
            disabled={linesBusy}
            onClick={(e) => {
              stopClick(e);
              onPostponeRow?.(data);
            }}
          >
            Postpone
          </button>
          <button
            type="button"
            className="btn btn-sm btn-outline-danger"
            disabled={linesBusy}
            onClick={(e) => {
              stopClick(e);
              onCancelRow?.(data);
            }}
          >
            Cancel
          </button>
        </>
      ) : null}
    </div>
  );
}

function formatIsoDateIn(d) {
  if (!d || typeof d !== "string") return "—";
  const parts = d.split("-");
  if (parts.length !== 3) return d;
  const [y, m, day] = parts;
  return `${day}-${m}-${y}`;
}

function yyyyMmToday() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
}

function todayIsoYyyyMmDd() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

/** Returns N consecutive yyyy-mm keys starting at the current month. */
function upcomingMonthKeys(count) {
  const out = [];
  const now = new Date();
  for (let i = 0; i < count; i++) {
    const d = new Date(now.getFullYear(), now.getMonth() + i, 1);
    out.push(`${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`);
  }
  return out;
}

function formatMillisIst(ms) {
  if (ms == null) return "—";
  try {
    return new Intl.DateTimeFormat("en-IN", {
      dateStyle: "medium",
      timeStyle: "short",
      timeZone: "Asia/Kolkata",
    }).format(new Date(Number(ms)));
  } catch {
    return "—";
  }
}

function banquetLineToEditDraft(ln) {
  const iso =
    ln?.eventDate == null
      ? ""
      : typeof ln.eventDate === "string"
        ? ln.eventDate.slice(0, 10)
        : "";
  const nstr = (v) => (v != null && v !== "" ? String(v) : "");
  return {
    id: ln?.id,
    eventDate: iso,
    serialNo: nstr(ln?.serialNo),
    packageName: ln?.packageName || "",
    slotDesc: ln?.slotDesc || "",
    numPax: nstr(ln?.numPax),
    advanceRupee: nstr(ln?.advanceRupee),
    balanceRupee: nstr(ln?.balanceRupee),
    totalAmountRupee: nstr(ln?.totalAmountRupee),
    bankPaymentRupee: nstr(ln?.bankPaymentRupee),
    cashPaymentRupee: nstr(ln?.cashPaymentRupee),
    status: ln?.status || "",
    remarks: ln?.remarks || "",
    decorExpensesRupee: nstr(ln?.decorExpensesRupee),
    decorCostRupee: nstr(ln?.decorCostRupee),
    prePlateCostRupee: nstr(ln?.prePlateCostRupee),
    customerName: ln?.customerName || "",
    customerEmail: ln?.customerEmail || "",
    customerPhone: ln?.customerPhone || "",
    addons: Array.isArray(ln?.addons)
      ? ln.addons.map((a) => ({ name: a?.name || "", amountRupee: nstr(a?.amountRupee) }))
      : [],
    cancelled: Boolean(ln?.cancelled),
  };
}

function emptyCreateDraft() {
  return {
    eventDate: "",
    numPax: "",
    prePlateCostRupee: "",
    advanceRupee: "",
    packageName: "",
    slotDesc: "",
    status: "",
    remarks: "",
    customerName: "",
    customerEmail: "",
    customerPhone: "",
    addons: [],
    sendConfirmationEmail: false,
  };
}

/** Sum of valid add-on amounts (rupees) on a create draft. */
function sumAddonAmounts(addons) {
  if (!Array.isArray(addons)) return 0;
  return addons.reduce((acc, a) => {
    const n = numField(a?.amountRupee);
    return acc + (n != null && n > 0 ? n : 0);
  }, 0);
}

/** Add-ons ready for the API: named lines with a positive amount. */
function cleanAddons(addons) {
  if (!Array.isArray(addons)) return [];
  return addons
    .map((a) => ({ name: String(a?.name || "").trim(), amountRupee: numField(a?.amountRupee) }))
    .filter((a) => a.name && a.amountRupee != null && a.amountRupee > 0);
}

function numField(s) {
  if (s == null || s === "") return null;
  const n = Number(s);
  return Number.isFinite(n) ? Math.round(n) : null;
}

function sintField(s) {
  const n = numField(s);
  return n === null ? null : n;
}

/** Total = (No. of Pax × pre-plate) + add-ons; remaining = total − advance (min 0). */
function computeBanquetAmounts(numPaxRaw, prePlateRaw, advanceRaw, addonsTotal = 0) {
  const pax = sintField(numPaxRaw);
  const prePlate = numField(prePlateRaw);
  const advance = numField(advanceRaw);
  if (pax == null || pax <= 0 || prePlate == null || prePlate <= 0) {
    return { totalAmount: null, remainingBalance: null };
  }
  const totalAmount = pax * prePlate + (Number(addonsTotal) || 0);
  const remainingBalance = Math.max(0, totalAmount - (advance != null && advance >= 0 ? advance : 0));
  return { totalAmount, remainingBalance };
}

function validateCreateDraft(d) {
  if (!String(d.eventDate || "").trim()) return "Event date is required.";
  if (!String(d.packageName || "").trim()) return "Function hall is required.";
  if (!String(d.slotDesc || "").trim()) return "Slot is required.";
  const pax = sintField(d.numPax);
  if (pax == null || pax <= 0) return "No. of Pax is required.";
  const prePlate = numField(d.prePlateCostRupee);
  if (prePlate == null || prePlate <= 0) return "Pre-plate cost is required.";
  if (d.advanceRupee === "" || d.advanceRupee == null) return "Advance is required.";
  const advance = numField(d.advanceRupee);
  if (advance == null || advance < 0) return "Advance must be a valid amount.";
  if (Array.isArray(d.addons)) {
    for (const a of d.addons) {
      if (!String(a?.name || "").trim()) return "Add-on name is required for each selected add-on.";
      const amt = numField(a?.amountRupee);
      if (amt == null || amt <= 0) return `Enter an amount for add-on "${a.name}".`;
    }
  }
  if (!String(d.remarks || "").trim()) return "Remarks / commitments is required.";
  if (!String(d.customerName || "").trim()) return "Customer name is required.";
  if (!String(d.customerPhone || "").trim()) return "Customer phone is required.";
  if (d.sendConfirmationEmail && !String(d.customerEmail || "").trim()) {
    return "Add a customer email before sending confirmation.";
  }
  return null;
}

function buildPatchBody(d, includeCancelled) {
  const body = {
    eventDate: d.eventDate || null,
    serialNo:
      d.serialNo === ""
        ? null
        : (() => {
            const v = Number.parseInt(String(d.serialNo), 10);
            return Number.isNaN(v) ? null : v;
          })(),
    packageName: d.packageName || null,
    slotDesc: d.slotDesc || null,
    numPax: sintField(d.numPax),
    advanceRupee: numField(d.advanceRupee),
    balanceRupee: numField(d.balanceRupee),
    totalAmountRupee: numField(d.totalAmountRupee),
    bankPaymentRupee: numField(d.bankPaymentRupee),
    cashPaymentRupee: numField(d.cashPaymentRupee),
    status: d.status || null,
    remarks: d.remarks || null,
    addons: cleanAddons(d.addons),
    decorExpensesRupee: numField(d.decorExpensesRupee),
    decorCostRupee: numField(d.decorCostRupee),
    prePlateCostRupee: numField(d.prePlateCostRupee),
    customerName: d.customerName || null,
    customerEmail: d.customerEmail || null,
    customerPhone: d.customerPhone || null,
  };
  if (includeCancelled) {
    body.cancelled = d.cancelled;
  }
  return body;
}

/** ADMIN / SUPER_ADMIN: banquet lines (no analytics OTP); create, edit, confirmation email */
export default function AdminBanquetHall() {
  const navigate = useNavigate();
  const { context } = useContext(Context);
  const gridRef = useRef(null);

  const role = String(context?.adminRole || "").toUpperCase();
  const isSuperAdmin = role === SUPER_ADMIN;
  const adminHeaders = useMemo(
    () => ({
      [ROLE_HEADER]: role,
    }),
    [role]
  );

  /**
   * When ON, fetch a forward window of months and hide rows whose event date is before today.
   * Plain ADMIN users are locked into upcoming-only; only SUPER_ADMIN can switch this off
   * to view historical bookings.
   */
  const [upcomingOnly, setUpcomingOnly] = useState(true);
  /** Number of months to fetch when {@code upcomingOnly} is on (1 / 3 / 6 / 12). */
  const [upcomingMonths, setUpcomingMonths] = useState(3);
  const [calendarMonth, setCalendarMonth] = useState(yyyyMmToday);
  const [rows, setRows] = useState([]);
  const [linesBusy, setLinesBusy] = useState(false);
  const [banner, setBanner] = useState(null);
  const [createDraft, setCreateDraft] = useState(() => emptyCreateDraft());
  const [createBusy, setCreateBusy] = useState(false);
  const [createOpen, setCreateOpen] = useState(false);

  const [editOpen, setEditOpen] = useState(false);
  const [editDraft, setEditDraft] = useState(null);
  const [editBusy, setEditBusy] = useState(false);
  const [sendingId, setSendingId] = useState(null);

  const [cancelOpen, setCancelOpen] = useState(false);
  const [cancelTarget, setCancelTarget] = useState(null);
  const [cancelPersonName, setCancelPersonName] = useState("");
  const [cancelMessage, setCancelMessage] = useState("");
  const [cancelBusy, setCancelBusy] = useState(false);

  const [postponeOpen, setPostponeOpen] = useState(false);
  const [postponeTarget, setPostponeTarget] = useState(null);
  const [postponePersonName, setPostponePersonName] = useState("");
  const [postponeMessage, setPostponeMessage] = useState("");
  const [postponeNewDate, setPostponeNewDate] = useState("");
  const [postponeBusy, setPostponeBusy] = useState(false);

  useEffect(() => {
    if (!context.isAdminLoggedIn) {
      navigate("/admin", { replace: true });
      return;
    }
    if (!ADMIN_ROLES.has(role)) {
      navigate("/admin/dashboard", { replace: true });
    }
  }, [context.isAdminLoggedIn, role, navigate]);

  // Plain admins cannot see historical bookings; force the upcoming-only filter on.
  useEffect(() => {
    if (!isSuperAdmin && !upcomingOnly) {
      setUpcomingOnly(true);
    }
  }, [isSuperAdmin, upcomingOnly]);

  const showBanner = useCallback((variant, message) => {
    setBanner({ variant, message });
    window.setTimeout(() => setBanner(null), 6000);
  }, []);

  const fetchMonthRows = useCallback(
    async (monthYyyyMm) => {
      try {
        const { data } = await axios.get(Constants.ADMIN_BANQUET_BOOKINGS_BASE, {
          params: { calendarMonth: monthYyyyMm },
          headers: { ...adminHeaders },
        });
        return Array.isArray(data) ? data : [];
      } catch (e) {
        showBanner("danger", e?.response?.data?.message || e?.message || `Could not load ${monthYyyyMm}.`);
        return [];
      }
    },
    [adminHeaders, showBanner]
  );

  const loadLines = useCallback(
    async (monthOverride) => {
      // Plain admins never bypass the upcoming filter, regardless of state changes.
      const effectiveUpcoming = upcomingOnly || !isSuperAdmin;
      setLinesBusy(true);
      try {
        if (effectiveUpcoming && !monthOverride) {
          const months = upcomingMonthKeys(Math.max(1, upcomingMonths));
          const batches = await Promise.all(months.map((m) => fetchMonthRows(m)));
          setRows(batches.flat());
        } else {
          const m = monthOverride ?? calendarMonth;
          setRows(await fetchMonthRows(m));
        }
      } finally {
        setLinesBusy(false);
      }
    },
    [upcomingOnly, isSuperAdmin, upcomingMonths, calendarMonth, fetchMonthRows]
  );

  useEffect(() => {
    if (!context.isAdminLoggedIn || !ADMIN_ROLES.has(role)) return;
    loadLines();
  }, [context.isAdminLoggedIn, role, loadLines]);

  /**
   * Hide past dates whenever the upcoming filter is on; ADMIN role is forced into
   * upcoming-only regardless of the toggle state. Always sort by event date ascending.
   */
  const visibleRows = useMemo(() => {
    const list = Array.isArray(rows) ? [...rows] : [];
    list.sort((a, b) => String(a.eventDate || "").localeCompare(String(b.eventDate || "")));
    const effectiveUpcoming = upcomingOnly || !isSuperAdmin;
    if (!effectiveUpcoming) return list;
    const today = todayIsoYyyyMmDd();
    return list.filter((ln) => {
      const ed = typeof ln?.eventDate === "string" ? ln.eventDate.slice(0, 10) : "";
      return ed && ed >= today;
    });
  }, [rows, upcomingOnly, isSuperAdmin]);

  const onCreate = async () => {
    const validationError = validateCreateDraft(createDraft);
    if (validationError) {
      showBanner("warning", validationError);
      return;
    }
    const addonsPayload = cleanAddons(createDraft.addons);
    const { totalAmount } = computeBanquetAmounts(
      createDraft.numPax,
      createDraft.prePlateCostRupee,
      createDraft.advanceRupee,
      sumAddonAmounts(createDraft.addons)
    );
    setCreateBusy(true);
    try {
      const { data } = await axios.post(
        Constants.ADMIN_BANQUET_BOOKINGS_BASE,
        {
          eventDate: createDraft.eventDate || null,
          numPax: sintField(createDraft.numPax),
          prePlateCostRupee: numField(createDraft.prePlateCostRupee),
          advanceRupee: numField(createDraft.advanceRupee),
          totalAmountRupee: totalAmount,
          packageName: createDraft.packageName || null,
          slotDesc: createDraft.slotDesc || null,
          status: createDraft.status || null,
          remarks: createDraft.remarks || null,
          addons: addonsPayload,
          customerName: createDraft.customerName || null,
          customerEmail: createDraft.customerEmail || null,
          customerPhone: createDraft.customerPhone || null,
          sendConfirmationEmail: Boolean(createDraft.sendConfirmationEmail),
        },
        { headers: { ...adminHeaders } }
      );
      const mk = data?.calendarMonthPeriodKey;
      if (mk && typeof mk === "string") {
        setCalendarMonth(mk);
      }
      setCreateDraft(emptyCreateDraft());
      setCreateOpen(false);
      showBanner("success", "Booking saved.");
      await loadLines(mk && typeof mk === "string" ? mk : undefined);
    } catch (e) {
      showBanner("danger", e?.response?.data?.message || e?.message || "Create failed.");
    } finally {
      setCreateBusy(false);
    }
  };

  const onSaveEdit = async () => {
    const d = editDraft;
    if (!d?.id || !d.eventDate) return;
    setEditBusy(true);
    try {
      await axios.put(Constants.adminBanquetBookingPutUrl(d.id), buildPatchBody(d, false), {
        headers: { ...adminHeaders },
      });
      setEditOpen(false);
      showBanner("success", "Row updated.");
      await loadLines();
    } catch (e) {
      showBanner("danger", e?.response?.data?.message || e?.message || "Update failed.");
    } finally {
      setEditBusy(false);
    }
  };

  const onSendConfirmation = useCallback(async (id) => {
    setSendingId(id);
    try {
      await axios.post(Constants.adminBanquetBookingSendConfirmationUrl(id), {}, { headers: { ...adminHeaders } });
      showBanner("success", "Confirmation email queued.");
      await loadLines();
    } catch (e) {
      showBanner("danger", e?.response?.data?.message || e?.message || "Could not send email.");
    } finally {
      setSendingId(null);
    }
  }, [adminHeaders, loadLines, showBanner]);

  const onEditRow = useCallback((ln) => {
    setEditDraft(banquetLineToEditDraft(ln));
    setEditOpen(true);
  }, []);

  const onCancelRow = useCallback((ln) => {
    setCancelTarget(ln);
    setCancelPersonName(context?.adminUserName || "");
    setCancelMessage("");
    setCancelOpen(true);
  }, [context?.adminUserName]);

  const onPostponeRow = useCallback((ln) => {
    setPostponeTarget(ln);
    setPostponePersonName(context?.adminUserName || "");
    setPostponeMessage("");
    const ed =
      typeof ln?.eventDate === "string" ? ln.eventDate.slice(0, 10) : "";
    setPostponeNewDate(ed);
    setPostponeOpen(true);
  }, [context?.adminUserName]);

  const submitCancel = async () => {
    if (!cancelTarget?.id) return;
    const personName = cancelPersonName.trim();
    const message = cancelMessage.trim();
    if (!personName || !message) {
      showBanner("warning", "Your name and message are required.");
      return;
    }
    setCancelBusy(true);
    try {
      await axios.post(
        Constants.adminBanquetBookingCancelUrl(cancelTarget.id),
        { personName, message },
        { headers: { ...adminHeaders } }
      );
      setCancelOpen(false);
      setCancelTarget(null);
      showBanner("success", "Booking cancelled.");
      await loadLines();
    } catch (e) {
      showBanner("danger", e?.response?.data?.message || e?.message || "Cancel failed.");
    } finally {
      setCancelBusy(false);
    }
  };

  const submitPostpone = async () => {
    if (!postponeTarget?.id) return;
    const personName = postponePersonName.trim();
    const message = postponeMessage.trim();
    if (!personName || !message || !postponeNewDate) {
      showBanner("warning", "Your name, message, and new event date are required.");
      return;
    }
    setPostponeBusy(true);
    try {
      await axios.post(
        Constants.adminBanquetBookingPostponeUrl(postponeTarget.id),
        { personName, message, newEventDate: postponeNewDate },
        { headers: { ...adminHeaders } }
      );
      setPostponeOpen(false);
      setPostponeTarget(null);
      showBanner("success", "Booking postponed.");
      await loadLines();
    } catch (e) {
      showBanner("danger", e?.response?.data?.message || e?.message || "Postpone failed.");
    } finally {
      setPostponeBusy(false);
    }
  };

  const createTotals = useMemo(
    () =>
      computeBanquetAmounts(
        createDraft.numPax,
        createDraft.prePlateCostRupee,
        createDraft.advanceRupee,
        sumAddonAmounts(createDraft.addons)
      ),
    [createDraft.numPax, createDraft.prePlateCostRupee, createDraft.advanceRupee, createDraft.addons]
  );

  const gridContext = useMemo(
    () => ({
      onEditRow,
      onSendConfirmation,
      onCancelRow,
      onPostponeRow,
      sendingId,
      linesBusy,
      isSuperAdmin,
    }),
    [onEditRow, onSendConfirmation, onCancelRow, onPostponeRow, sendingId, linesBusy, isSuperAdmin]
  );

  const banquetColumnDefs = useMemo(
    () => [
      {
        headerName: "Date",
        field: "eventDate",
        width: 110,
        valueFormatter: (p) => formatIsoDateIn(p.value),
      },
      {
        headerName: "Customer",
        width: 165,
        wrapText: true,
        autoHeight: true,
        cellRenderer: BanquetCustomerCell,
      },
      {
        headerName: "Contact",
        width: 155,
        wrapText: true,
        autoHeight: true,
        cellRenderer: BanquetContactCell,
      },
      {
        headerName: "Function hall",
        width: 140,
        wrapText: true,
        autoHeight: true,
        cellRenderer: BanquetHallCell,
      },
      {
        headerName: "No. of Pax",
        field: "numPax",
        width: 72,
        minWidth: 68,
        maxWidth: 80,
        type: "numericColumn",
        wrapHeaderText: true,
        autoHeaderHeight: true,
      },
      {
        headerName: "Pre-plate",
        field: "prePlateCostRupee",
        width: 88,
        minWidth: 82,
        maxWidth: 96,
        type: "numericColumn",
        wrapHeaderText: true,
        autoHeaderHeight: true,
        valueFormatter: (p) => formatRupeeMaybe(p.value),
      },
      {
        headerName: "Advance amount",
        field: "advanceRupee",
        width: 100,
        minWidth: 92,
        type: "numericColumn",
        wrapHeaderText: true,
        autoHeaderHeight: true,
        valueFormatter: (p) => formatRupeeMaybe(p.value),
        cellClass: "admin-console-grid-cell-advance",
      },
      {
        headerName: "Remaining amount",
        field: "balanceRupee",
        width: 112,
        minWidth: 100,
        type: "numericColumn",
        wrapHeaderText: true,
        autoHeaderHeight: true,
        valueFormatter: (p) => formatRupeeMaybe(p.value),
        cellClass: "admin-console-grid-cell-amount",
      },
      {
        headerName: "Total amount",
        field: "totalAmountRupee",
        width: 100,
        minWidth: 92,
        type: "numericColumn",
        wrapHeaderText: true,
        autoHeaderHeight: true,
        valueFormatter: (p) => formatRupeeMaybe(p.value),
        cellClass: "admin-console-grid-cell-amount",
      },
      { headerName: "S.No", field: "serialNo", width: 72 },
      {
        headerName: "Src",
        field: "source",
        width: 90,
        valueFormatter: (p) => {
          const src = p.value || "—";
          return p.data?.cancelled ? `${src} (CX)` : src;
        },
      },
      {
        headerName: "Bank",
        field: "bankPaymentRupee",
        width: 100,
        type: "numericColumn",
        valueFormatter: (p) => formatRupeeMaybe(p.value),
      },
      {
        headerName: "Cash",
        field: "cashPaymentRupee",
        width: 100,
        type: "numericColumn",
        valueFormatter: (p) => formatRupeeMaybe(p.value),
      },
      { headerName: "Status", field: "status", width: 100 },
      { headerName: "Remarks / commitments", field: "remarks", width: 200, wrapText: true, autoHeight: true },
      {
        headerName: "Décor exp",
        field: "decorExpensesRupee",
        width: 110,
        type: "numericColumn",
        valueFormatter: (p) => formatRupeeMaybe(p.value),
      },
      {
        headerName: "Décor cost",
        field: "decorCostRupee",
        width: 110,
        type: "numericColumn",
        valueFormatter: (p) => formatRupeeMaybe(p.value),
      },
      {
        headerName: "Confirm sent",
        field: "confirmationSentAtMillis",
        width: 150,
        valueFormatter: (p) => formatMillisIst(p.value),
      },
      { headerName: "Import file", field: "importedFromFile", width: 140 },
      {
        headerName: "Actions",
        pinned: "right",
        width: isSuperAdmin ? 132 : 120,
        wrapText: true,
        autoHeight: true,
        sortable: false,
        filter: false,
        cellClass: "admin-console-grid-actions-cell",
        cellRenderer: BanquetActionsCell,
        suppressNavigable: true,
      },
    ],
    [isSuperAdmin]
  );

  const activeCount = visibleRows.filter((x) => !x.cancelled).length;
  const effectiveUpcoming = upcomingOnly || !isSuperAdmin;
  const noRowsOverlay = effectiveUpcoming
    ? `No upcoming bookings in the next ${upcomingMonths} ${upcomingMonths === 1 ? "month" : "months"}.`
    : "No rows for this month.";
  const hiddenPastCount = effectiveUpcoming
    ? Math.max(0, (Array.isArray(rows) ? rows.length : 0) - visibleRows.length)
    : 0;

  return (
    <AdminLayout title="Banquet hall">
      {banner ? (
        <Alert variant={banner.variant} className="py-2" dismissible onClose={() => setBanner(null)}>
          {banner.message}
        </Alert>
      ) : null}

      <div className="admin-console-card p-3 rounded border border-secondary">
        <div className="d-flex flex-wrap justify-content-between align-items-center gap-2 mb-3">
          <div>
            <h2 className="h6 text-info mb-1">{effectiveUpcoming ? "Upcoming bookings" : "Month lines"}</h2>
            <p className="small text-secondary mb-0">
              {effectiveUpcoming ? (
                <>
                  Next <strong>{upcomingMonths}</strong>{" "}
                  {upcomingMonths === 1 ? "month" : "months"} — active rows: <strong>{activeCount}</strong>
                  {hiddenPastCount > 0 ? (
                    <span className="text-secondary">
                      {" "}
                      ({hiddenPastCount} past hidden)
                    </span>
                  ) : null}
                </>
              ) : (
                <>
                  Calendar month <code className="text-info">{calendarMonth}</code> — active:{" "}
                  <strong>{activeCount}</strong>
                </>
              )}
            </p>
          </div>
          <Button
            size="sm"
            variant="success"
            type="button"
            onClick={() => {
              setCreateDraft(emptyCreateDraft());
              setCreateOpen(true);
            }}
          >
            Banquet Hall Booking
          </Button>
        </div>
        <div className="d-flex flex-wrap align-items-end gap-3 mb-3">
          <div className="d-flex flex-column">
            <Form.Check
              type="switch"
              id="pb-banquet-upcoming-only"
              className="text-white-50"
              label="Upcoming only"
              checked={effectiveUpcoming}
              disabled={!isSuperAdmin}
              onChange={(e) => {
                if (!isSuperAdmin) return;
                setUpcomingOnly(Boolean(e.target.checked));
              }}
              title={
                isSuperAdmin
                  ? "Toggle to switch between upcoming-only and a specific month."
                  : "Locked to upcoming bookings. Only SUPER_ADMIN can view past records."
              }
            />
            {!isSuperAdmin ? (
              <small className="text-secondary mt-1">Locked for ADMIN — only upcoming events are visible.</small>
            ) : null}
          </div>
          {effectiveUpcoming ? (
            <div>
              <Form.Label className="text-secondary small mb-1">Window</Form.Label>
              <Form.Select
                size="sm"
                className="text-bg-dark border-secondary"
                style={{ width: 180 }}
                value={String(upcomingMonths)}
                onChange={(e) => setUpcomingMonths(Number(e.target.value) || 3)}
              >
                <option value="1">This month only</option>
                <option value="3">Next 3 months</option>
                <option value="6">Next 6 months</option>
                <option value="12">Next 12 months</option>
              </Form.Select>
            </div>
          ) : (
            <div>
              <Form.Label className="text-secondary small mb-1">Calendar month</Form.Label>
              <AdminDateInput
                inputType="month"
                size="sm"
                style={{ width: 180 }}
                value={calendarMonth}
                onChange={(e) => setCalendarMonth(e.target.value)}
              />
            </div>
          )}
          <Button size="sm" variant="outline-light" type="button" disabled={linesBusy} onClick={() => loadLines()}>
            Refresh
          </Button>
          {linesBusy ? <Spinner animation="border" size="sm" className="text-light" /> : null}
        </div>

        <div className="admin-console-card-title px-1 mb-2">
          Banquet bookings — use Edit or Email in the actions column
        </div>
        <div className="admin-console-grid-wrap admin-console-grid-wrap--light ag-theme-quartz" style={{ height: 520 }}>
          <AgGridReact
            ref={gridRef}
            rowData={visibleRows}
            columnDefs={banquetColumnDefs}
            context={gridContext}
            rowSelection="single"
            rowHeight={68}
            autoHeaderHeight
            suppressExcelExport
            suppressCellFocus
            overlayNoRowsTemplate={`<span class="ag-overlay-no-rows-center small text-secondary">${noRowsOverlay}</span>`}
            getRowClass={(params) => (params.data?.cancelled ? "opacity-60" : "")}
            defaultColDef={{
              sortable: true,
              filter: false,
              resizable: true,
              suppressMovable: true,
            }}
          />
        </div>
      </div>

      <Modal
        show={createOpen}
        onHide={() => !createBusy && setCreateOpen(false)}
        centered
        size="lg"
        className="admin-console-modal pb-banquet-modal"
        backdrop={createBusy ? "static" : true}
      >
        <Modal.Header closeButton>
          <Modal.Title>Banquet Hall Booking</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form
            id="pb-banquet-admin-create"
            className="row g-2 small"
            onSubmit={(e) => {
              e.preventDefault();
              onCreate();
            }}
          >
            <div className="col-md-4">
              <Form.Label>Event date *</Form.Label>
              <AdminDateInput
                size="sm"
                required
                value={createDraft.eventDate}
                onChange={(e) => setCreateDraft({ ...createDraft, eventDate: e.target.value })}
              />
            </div>
            <div className="col-md-4">
              <Form.Label>Function hall *</Form.Label>
              <Form.Select
                size="sm"
                required
                value={createDraft.packageName}
                onChange={(e) => setCreateDraft({ ...createDraft, packageName: e.target.value })}
              >
                <option value="">Select hall…</option>
                {FUNCTION_HALL_OPTIONS.map((h) => (
                  <option key={h} value={h}>
                    {h}
                  </option>
                ))}
              </Form.Select>
            </div>
            <div className="col-md-4">
              <Form.Label>Slot *</Form.Label>
              <Form.Select
                size="sm"
                required
                value={createDraft.slotDesc}
                onChange={(e) => setCreateDraft({ ...createDraft, slotDesc: e.target.value })}
              >
                <option value="">Select slot…</option>
                {BANQUET_SLOT_OPTIONS.map((s) => (
                  <option key={s} value={s}>
                    {s}
                  </option>
                ))}
              </Form.Select>
            </div>
            <div className="col-md-4">
              <Form.Label>No. of Pax *</Form.Label>
              <Form.Control
                type="number"
                size="sm"
                required
                min={1}
                value={createDraft.numPax}
                onChange={(e) => setCreateDraft({ ...createDraft, numPax: e.target.value })}
              />
            </div>
            <div className="col-md-4">
              <Form.Label>Pre-plate ₹ *</Form.Label>
              <Form.Control
                type="number"
                size="sm"
                required
                min={1}
                value={createDraft.prePlateCostRupee}
                onChange={(e) => setCreateDraft({ ...createDraft, prePlateCostRupee: e.target.value })}
              />
            </div>
            <div className="col-md-4">
              <Form.Label>Advance ₹ *</Form.Label>
              <Form.Control
                type="number"
                size="sm"
                required
                min={0}
                value={createDraft.advanceRupee}
                onChange={(e) => setCreateDraft({ ...createDraft, advanceRupee: e.target.value })}
              />
            </div>
            <div className="col-md-6">
              <Form.Label>Total amount</Form.Label>
              <div className="admin-console-grid-cell-amount fs-6">
                {createTotals.totalAmount != null ? formatInr(createTotals.totalAmount) : "—"}
              </div>
            </div>
            <div className="col-md-6">
              <Form.Label>Remaining balance</Form.Label>
              <div className="admin-console-grid-cell-amount fs-6">
                {createTotals.remainingBalance != null ? formatInr(createTotals.remainingBalance) : "—"}
              </div>
            </div>
            <div className="col-12">
              <Form.Label>Add-ons</Form.Label>
              <Form.Select
                size="sm"
                value=""
                onChange={(e) => {
                  const picked = e.target.value;
                  if (!picked) return;
                  const isOther = picked === "Other";
                  const existing = Array.isArray(createDraft.addons) ? createDraft.addons : [];
                  if (!isOther && existing.some((a) => a.name === picked)) return;
                  setCreateDraft({
                    ...createDraft,
                    addons: [...existing, { name: isOther ? "" : picked, amountRupee: "" }],
                  });
                }}
              >
                <option value="">Select add-on…</option>
                {BANQUET_ADDON_OPTIONS.map((o) => (
                  <option key={o} value={o}>
                    {o === "Other" ? "Other's" : o}
                  </option>
                ))}
              </Form.Select>
              {Array.isArray(createDraft.addons) && createDraft.addons.length > 0 ? (
                <div className="row g-2 mt-1">
                  {createDraft.addons.map((addon, idx) => {
                    const isOther = !BANQUET_ADDON_OPTIONS.includes(addon.name) || addon.name === "";
                    return (
                      <div className="col-md-6" key={idx}>
                        <div className="d-flex align-items-end gap-2">
                          <div className="flex-grow-1">
                            {isOther ? (
                              <>
                                <Form.Label className="mb-1">Add-on name *</Form.Label>
                                <Form.Control
                                  size="sm"
                                  placeholder="e.g. Special décor"
                                  value={addon.name}
                                  onChange={(e) => {
                                    const next = [...createDraft.addons];
                                    next[idx] = { ...next[idx], name: e.target.value };
                                    setCreateDraft({ ...createDraft, addons: next });
                                  }}
                                />
                              </>
                            ) : (
                              <Form.Label className="mb-1">{addon.name} amount ₹ *</Form.Label>
                            )}
                            <Form.Control
                              type="number"
                              size="sm"
                              min={0}
                              placeholder="Amount ₹"
                              value={addon.amountRupee}
                              onChange={(e) => {
                                const next = [...createDraft.addons];
                                next[idx] = { ...next[idx], amountRupee: e.target.value };
                                setCreateDraft({ ...createDraft, addons: next });
                              }}
                            />
                          </div>
                          <Button
                            variant="outline-danger"
                            size="sm"
                            className="mb-1"
                            onClick={() => {
                              const next = createDraft.addons.filter((_, i) => i !== idx);
                              setCreateDraft({ ...createDraft, addons: next });
                            }}
                          >
                            Remove
                          </Button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : null}
            </div>
            <div className="col-12">
              <Form.Label>Remarks / commitments *</Form.Label>
              <Form.Control
                as="textarea"
                rows={3}
                size="sm"
                required
                value={createDraft.remarks}
                onChange={(e) => setCreateDraft({ ...createDraft, remarks: e.target.value })}
              />
            </div>
            <div className="col-md-4">
              <Form.Label>Customer name *</Form.Label>
              <Form.Control
                size="sm"
                required
                value={createDraft.customerName}
                onChange={(e) => setCreateDraft({ ...createDraft, customerName: e.target.value })}
              />
            </div>
            <div className="col-md-4">
              <Form.Label>Customer email</Form.Label>
              <Form.Control
                type="email"
                size="sm"
                value={createDraft.customerEmail}
                onChange={(e) => setCreateDraft({ ...createDraft, customerEmail: e.target.value })}
              />
            </div>
            <div className="col-md-4">
              <Form.Label>Customer phone *</Form.Label>
              <Form.Control
                size="sm"
                required
                value={createDraft.customerPhone}
                onChange={(e) => setCreateDraft({ ...createDraft, customerPhone: e.target.value })}
              />
            </div>
            <div className="col-12">
              <Form.Check
                type="switch"
                id="pb-banquet-send-on-create"
                className="text-dark"
                label="Send confirmation email now"
                checked={createDraft.sendConfirmationEmail}
                onChange={(e) =>
                  setCreateDraft({ ...createDraft, sendConfirmationEmail: Boolean(e.target.checked) })
                }
              />
            </div>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" size="sm" disabled={createBusy} onClick={() => setCreateOpen(false)}>
            Cancel
          </Button>
          <Button
            variant="success"
            size="sm"
            type="submit"
            form="pb-banquet-admin-create"
            disabled={createBusy}
          >
            {createBusy ? (
              <>
                <Spinner animation="border" size="sm" className="me-1 align-middle" /> Saving…
              </>
            ) : (
              "Save booking"
            )}
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal
        show={editOpen}
        onHide={() => !editBusy && setEditOpen(false)}
        centered
        size="lg"
        className="admin-console-modal pb-banquet-modal"
        backdrop={editBusy ? "static" : true}
      >
        <Modal.Header closeButton>
          <Modal.Title className="h6">
            Edit banquet line {editDraft?.id != null ? `#${editDraft.id}` : ""}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {editDraft ? (
            <Form id="pb-banquet-admin-edit" className="row g-2 small">
              <div className="col-md-4">
                <Form.Label>Event date</Form.Label>
                <AdminDateInput
                  size="sm"
                  required
                  value={editDraft.eventDate || ""}
                  onChange={(e) => setEditDraft({ ...editDraft, eventDate: e.target.value })}
                />
              </div>
              <div className="col-md-4">
                <Form.Label>S.No</Form.Label>
                <Form.Control
                  type="number"
                  size="sm"
                  value={editDraft.serialNo}
                  onChange={(e) => setEditDraft({ ...editDraft, serialNo: e.target.value })}
                />
              </div>
              {editDraft.cancelled ? (
                <div className="col-md-4 d-flex align-items-end">
                  <span className="badge bg-danger mb-2">Cancelled</span>
                </div>
              ) : null}
              <div className="col-md-6">
                <Form.Label>Function hall</Form.Label>
                <Form.Select
                  size="sm"
                  value={editDraft.packageName}
                  onChange={(e) => setEditDraft({ ...editDraft, packageName: e.target.value })}
                >
                  <option value="">Select hall…</option>
                  {hallSelectOptions(editDraft.packageName).map((o) => (
                    <option key={o.value} value={o.value}>
                      {o.label}
                    </option>
                  ))}
                </Form.Select>
              </div>
              <div className="col-md-6">
                <Form.Label>Slot</Form.Label>
                <Form.Select
                  size="sm"
                  value={editDraft.slotDesc}
                  onChange={(e) => setEditDraft({ ...editDraft, slotDesc: e.target.value })}
                >
                  <option value="">Select slot…</option>
                  {slotSelectOptions(editDraft.slotDesc).map((o) => (
                    <option key={o.value} value={o.value}>
                      {o.label}
                    </option>
                  ))}
                </Form.Select>
              </div>
              <div className="col-md-3">
                <Form.Label>No. of Pax</Form.Label>
                <Form.Control
                  type="number"
                  size="sm"
                  value={editDraft.numPax}
                  onChange={(e) => setEditDraft({ ...editDraft, numPax: e.target.value })}
                />
              </div>
              <div className="col-md-3">
                <Form.Label>Advance ₹</Form.Label>
                <Form.Control
                  type="number"
                  size="sm"
                  value={editDraft.advanceRupee}
                  onChange={(e) => setEditDraft({ ...editDraft, advanceRupee: e.target.value })}
                />
              </div>
              <div className="col-md-3">
                <Form.Label>Balance ₹</Form.Label>
                <Form.Control
                  type="number"
                  size="sm"
                  value={editDraft.balanceRupee}
                  onChange={(e) => setEditDraft({ ...editDraft, balanceRupee: e.target.value })}
                />
              </div>
              <div className="col-md-3">
                <Form.Label>Total ₹</Form.Label>
                <Form.Control
                  type="number"
                  size="sm"
                  value={editDraft.totalAmountRupee}
                  onChange={(e) => setEditDraft({ ...editDraft, totalAmountRupee: e.target.value })}
                />
              </div>
              <div className="col-md-6">
                <Form.Label>Bank ₹</Form.Label>
                <Form.Control
                  type="number"
                  size="sm"
                  value={editDraft.bankPaymentRupee}
                  onChange={(e) => setEditDraft({ ...editDraft, bankPaymentRupee: e.target.value })}
                />
              </div>
              <div className="col-md-6">
                <Form.Label>Cash ₹</Form.Label>
                <Form.Control
                  type="number"
                  size="sm"
                  value={editDraft.cashPaymentRupee}
                  onChange={(e) => setEditDraft({ ...editDraft, cashPaymentRupee: e.target.value })}
                />
              </div>
              <div className="col-md-4">
                <Form.Label>Pre-plate / unit ₹</Form.Label>
                <Form.Control
                  type="number"
                  size="sm"
                  value={editDraft.prePlateCostRupee}
                  onChange={(e) => setEditDraft({ ...editDraft, prePlateCostRupee: e.target.value })}
                />
              </div>
              <div className="col-md-4">
                <Form.Label>Décor exp ₹</Form.Label>
                <Form.Control
                  type="number"
                  size="sm"
                  value={editDraft.decorExpensesRupee}
                  onChange={(e) => setEditDraft({ ...editDraft, decorExpensesRupee: e.target.value })}
                />
              </div>
              <div className="col-md-4">
                <Form.Label>Décor cost ₹</Form.Label>
                <Form.Control
                  type="number"
                  size="sm"
                  value={editDraft.decorCostRupee}
                  onChange={(e) => setEditDraft({ ...editDraft, decorCostRupee: e.target.value })}
                />
              </div>
              <div className="col-12">
                <Form.Label>Add-ons</Form.Label>
                <Form.Select
                  size="sm"
                  value=""
                  onChange={(e) => {
                    const picked = e.target.value;
                    if (!picked) return;
                    const isOther = picked === "Other";
                    const existing = Array.isArray(editDraft.addons) ? editDraft.addons : [];
                    if (!isOther && existing.some((a) => a.name === picked)) return;
                    setEditDraft({
                      ...editDraft,
                      addons: [...existing, { name: isOther ? "" : picked, amountRupee: "" }],
                    });
                  }}
                >
                  <option value="">Select add-on…</option>
                  {BANQUET_ADDON_OPTIONS.map((o) => (
                    <option key={o} value={o}>
                      {o === "Other" ? "Other's" : o}
                    </option>
                  ))}
                </Form.Select>
                {Array.isArray(editDraft.addons) && editDraft.addons.length > 0 ? (
                  <div className="row g-2 mt-1">
                    {editDraft.addons.map((addon, idx) => {
                      const isOther = !BANQUET_ADDON_OPTIONS.includes(addon.name) || addon.name === "";
                      return (
                        <div className="col-md-6" key={idx}>
                          <div className="d-flex align-items-end gap-2">
                            <div className="flex-grow-1">
                              {isOther ? (
                                <>
                                  <Form.Label className="mb-1">Add-on name *</Form.Label>
                                  <Form.Control
                                    size="sm"
                                    placeholder="e.g. Special décor"
                                    value={addon.name}
                                    onChange={(e) => {
                                      const next = [...editDraft.addons];
                                      next[idx] = { ...next[idx], name: e.target.value };
                                      setEditDraft({ ...editDraft, addons: next });
                                    }}
                                  />
                                </>
                              ) : (
                                <Form.Label className="mb-1">{addon.name} amount ₹ *</Form.Label>
                              )}
                              <Form.Control
                                type="number"
                                size="sm"
                                min={0}
                                placeholder="Amount ₹"
                                value={addon.amountRupee}
                                onChange={(e) => {
                                  const next = [...editDraft.addons];
                                  next[idx] = { ...next[idx], amountRupee: e.target.value };
                                  setEditDraft({ ...editDraft, addons: next });
                                }}
                              />
                            </div>
                            <Button
                              variant="outline-danger"
                              size="sm"
                              className="mb-1"
                              onClick={() => {
                                const next = editDraft.addons.filter((_, i) => i !== idx);
                                setEditDraft({ ...editDraft, addons: next });
                              }}
                            >
                              Remove
                            </Button>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                ) : null}
              </div>
              <div className="col-12">
                <Form.Label>Remarks / commitments</Form.Label>
                <Form.Control
                  as="textarea"
                  rows={3}
                  size="sm"
                  value={editDraft.remarks}
                  onChange={(e) => setEditDraft({ ...editDraft, remarks: e.target.value })}
                />
              </div>
              <div className="col-md-4">
                <Form.Label>Customer name</Form.Label>
                <Form.Control
                  size="sm"
                  value={editDraft.customerName}
                  onChange={(e) => setEditDraft({ ...editDraft, customerName: e.target.value })}
                />
              </div>
              <div className="col-md-4">
                <Form.Label>Customer email</Form.Label>
                <Form.Control
                  type="email"
                  size="sm"
                  value={editDraft.customerEmail}
                  onChange={(e) => setEditDraft({ ...editDraft, customerEmail: e.target.value })}
                />
              </div>
              <div className="col-md-4">
                <Form.Label>Customer phone</Form.Label>
                <Form.Control
                  size="sm"
                  value={editDraft.customerPhone}
                  onChange={(e) => setEditDraft({ ...editDraft, customerPhone: e.target.value })}
                />
              </div>
            </Form>
          ) : (
            <p className="text-muted small mb-0">No row loaded.</p>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" size="sm" disabled={editBusy} onClick={() => setEditOpen(false)}>
            Close
          </Button>
          <Button
            variant="primary"
            size="sm"
            disabled={editBusy || editDraft == null || !editDraft.id || !editDraft.eventDate}
            onClick={onSaveEdit}
          >
            {editBusy ? (
              <>
                <Spinner animation="border" size="sm" className="me-1 align-middle" /> Saving…
              </>
            ) : (
              "Save"
            )}
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal
        show={cancelOpen}
        onHide={() => !cancelBusy && setCancelOpen(false)}
        centered
        backdrop={cancelBusy ? "static" : true}
      >
        <Modal.Header closeButton closeVariant="white" className="bg-secondary border-secondary">
          <Modal.Title className="h6">Cancel banquet booking</Modal.Title>
        </Modal.Header>
        <Modal.Body className="text-bg-dark">
          <p className="small text-secondary">
            SUPER_ADMIN only. Ref{" "}
            <strong>{cancelTarget?.bookingReferenceId || cancelTarget?.id || "—"}</strong>
          </p>
          <Form.Group className="mb-3">
            <Form.Label className="text-secondary small">Your name</Form.Label>
            <Form.Control
              size="sm"
              className="text-bg-dark border-secondary"
              value={cancelPersonName}
              onChange={(e) => setCancelPersonName(e.target.value)}
            />
          </Form.Group>
          <Form.Group>
            <Form.Label className="text-secondary small">Message to customer / internal note</Form.Label>
            <Form.Control
              as="textarea"
              rows={3}
              size="sm"
              className="text-bg-dark border-secondary"
              value={cancelMessage}
              onChange={(e) => setCancelMessage(e.target.value)}
              placeholder="Reason for cancellation…"
            />
          </Form.Group>
        </Modal.Body>
        <Modal.Footer className="text-bg-dark border-secondary">
          <Button variant="secondary" size="sm" disabled={cancelBusy} onClick={() => setCancelOpen(false)}>
            Close
          </Button>
          <Button variant="danger" size="sm" disabled={cancelBusy} onClick={submitCancel}>
            {cancelBusy ? "Cancelling…" : "Confirm cancel"}
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal
        show={postponeOpen}
        onHide={() => !postponeBusy && setPostponeOpen(false)}
        centered
        backdrop={postponeBusy ? "static" : true}
      >
        <Modal.Header closeButton closeVariant="white" className="bg-secondary border-secondary">
          <Modal.Title className="h6">Postpone banquet booking</Modal.Title>
        </Modal.Header>
        <Modal.Body className="text-bg-dark">
          <p className="small text-secondary">
            SUPER_ADMIN only. Ref{" "}
            <strong>{postponeTarget?.bookingReferenceId || postponeTarget?.id || "—"}</strong>
          </p>
          <Form.Group className="mb-3">
            <Form.Label className="text-secondary small">New event date</Form.Label>
            <AdminDateInput
              size="sm"
              required
              value={postponeNewDate}
              onChange={(e) => setPostponeNewDate(e.target.value)}
            />
          </Form.Group>
          <Form.Group className="mb-3">
            <Form.Label className="text-secondary small">Your name</Form.Label>
            <Form.Control
              size="sm"
              className="text-bg-dark border-secondary"
              value={postponePersonName}
              onChange={(e) => setPostponePersonName(e.target.value)}
            />
          </Form.Group>
          <Form.Group>
            <Form.Label className="text-secondary small">Message / reason</Form.Label>
            <Form.Control
              as="textarea"
              rows={3}
              size="sm"
              className="text-bg-dark border-secondary"
              value={postponeMessage}
              onChange={(e) => setPostponeMessage(e.target.value)}
              placeholder="Reason for postponement…"
            />
          </Form.Group>
        </Modal.Body>
        <Modal.Footer className="text-bg-dark border-secondary">
          <Button variant="secondary" size="sm" disabled={postponeBusy} onClick={() => setPostponeOpen(false)}>
            Close
          </Button>
          <Button variant="warning" size="sm" disabled={postponeBusy} onClick={submitPostpone}>
            {postponeBusy ? "Saving…" : "Confirm postpone"}
          </Button>
        </Modal.Footer>
      </Modal>
    </AdminLayout>
  );
}
